import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useState } from 'react';
const FULLSCREEN_KEY = 'drawer-fullscreen';
export const ArtifactDrawerContext = /*#__PURE__*/ createContext(undefined);
/**
 * Provider component for artifact drawer context
 * Manages the open/close state, fullscreen mode, and selected artifact
 */ export function ArtifactDrawerProvider({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedArtifactId, setSelectedArtifactId] = useState(null);
    const [sourceContext, setSourceContext] = useState(null);
    const [sourceChatId, setSourceChatId] = useState(null);
    // Initialize fullscreen state from localStorage
    const [isFullscreen, setIsFullscreenState] = useState(()=>{
        try {
            const stored = localStorage.getItem(FULLSCREEN_KEY);
            return stored === 'true';
        } catch  {
            // localStorage access failed - default to false
            return false;
        }
    });
    const openDrawer = useCallback((artifactId, options)=>{
        setSelectedArtifactId(artifactId);
        setSourceContext(options?.sourceContext || 'chat');
        setSourceChatId(options?.chatId || null);
        setIsOpen(true);
    }, []);
    const closeDrawer = useCallback(()=>{
        setIsOpen(false);
        // Reset source context on close
        setSourceContext(null);
        setSourceChatId(null);
    }, []);
    const setFullscreen = useCallback((fullscreen)=>{
        setIsFullscreenState(fullscreen);
        try {
            localStorage.setItem(FULLSCREEN_KEY, String(fullscreen));
        } catch  {
        // localStorage access failed - silently ignore
        }
    }, []);
    const toggleFullscreen = useCallback(()=>{
        setFullscreen(!isFullscreen);
    }, [
        isFullscreen,
        setFullscreen
    ]);
    const value = {
        isOpen,
        isFullscreen,
        selectedArtifactId,
        sourceContext,
        sourceChatId,
        openDrawer,
        closeDrawer,
        toggleFullscreen,
        setFullscreen
    };
    return /*#__PURE__*/ _jsx(ArtifactDrawerContext.Provider, {
        value: value,
        children: children
    });
}
/**
 * Hook for accessing artifact drawer context
 * Must be used within ArtifactDrawerProvider
 * @returns The artifact drawer context
 * @throws Error if used outside of ArtifactDrawerProvider
 */ export function useArtifactDrawer() {
    const context = useContext(ArtifactDrawerContext);
    if (!context) {
        throw new Error('useArtifactDrawer must be used within ArtifactDrawerProvider');
    }
    return context;
}
